import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../lib/auth/axiosApi';
import Board from './Board';
import Replay from './Replay';
import Analysis from './Analysis';
import {
  legalNextMoves, destinationsFrom, applyMoves, maxDiceUsable, diceRemaining, legalPlays, BAR, OFF,
} from './engine';

const POLL_MS = 700;        // idle refresh
const FAST_POLL_MS = 300;   // while watching the opponent roll/move, live
const LEVEL = { 1: 'single', 2: 'gammon', 3: 'backgammon' };

const readPref = (k, d) => {
  try { const v = localStorage.getItem(`bg_${k}`); return v === null ? d : v === '1'; } catch (e) { return d; }
};
const writePref = (k, v) => { try { localStorage.setItem(`bg_${k}`, v ? '1' : '0'); } catch (e) { /* private mode */ } };

function fmtClock(s) {
  s = Math.max(0, Math.floor(s));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function nameOf(state, pid) {
  const p = (state.participants || []).find((x) => x.pid === pid);
  return p ? p.name : '?';
}

function describe(state, e) {
  const who = e.pid ? nameOf(state, e.pid) : '';
  switch (e.type) {
    case 'opening_tie': return `opening roll tie ${e.dice[0]}-${e.dice[1]}, re-roll`;
    case 'opening': return `opening roll ${e.dice[0]}-${e.dice[1]} — ${nameOf(state, (state.game.actors || [])[e.side])} goes first`;
    case 'move': return `${who} ${e.dice ? `${e.dice[0]}${e.dice[1]}` : ''}: ${e.text || "can't move"}`;
    case 'double': return `${who} doubles${e.target && e.target !== e.pid ? ` ${nameOf(state, e.target)}` : ''} to ${e.value}`;
    case 'take': return `${who} takes${e.target ? ` (${nameOf(state, e.target)}'s cube)` : ''}`;
    case 'drop': return `${who} drops${e.target ? ` (${nameOf(state, e.target)}'s cube)` : ''}`;
    case 'beaver': return `${who} beavers! cube at ${e.value}`;
    case 'resign': return `${who} offers to resign a ${LEVEL[e.level]}`;
    case 'accept_resign': return `${who} accepts the resignation`;
    case 'reject_resign': return `${who} rejects the resignation`;
    case 'captain': return e.text;
    case 'timeout': return `${who} ran out of time`;
    case 'game_over': return `${who} wins ${e.points} pt${e.points === 1 ? '' : 's'} (${LEVEL[e.level]}, ${e.reason})`;
    default: return null;
  }
}

export default function Table({ tableId, onExit, onOpenTable }) {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [diceSwap, setDiceSwap] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState('');
  const [teamChat, setTeamChat] = useState(false);
  const [autoConfirm, setAutoConfirm] = useState(readPref('autoconfirm', false));
  const [autoForced, setAutoForced] = useState(readPref('autoforced', true));
  const [showReplay, setShowReplay] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const isLoggedIn = (() => { try { return !!localStorage.getItem('access_token'); } catch (e) { return false; } })();
  const [resignOpen, setResignOpen] = useState(false);
  const [clockBase, setClockBase] = useState(null);
  const [tickNow, setTickNow] = useState(Date.now());
  const versionRef = useRef(null);
  const turnKeyRef = useRef(null);
  const autoForcedRef = useRef(null);
  const logRef = useRef(null);
  const chatRef = useRef(null);

  const ingest = useCallback((d) => {
    if (!d) return;
    if (d.unchanged) {
      if (d.clock) setClockBase({ clock: d.clock, at: Date.now() });
      return;
    }
    versionRef.current = d.version;
    setData(d);
    if (d.state && d.state.clock) setClockBase({ clock: d.state.clock, at: Date.now() });
  }, []);

  const fetchState = useCallback(async (full) => {
    try {
      const v = full || versionRef.current === null ? '' : `?v=${versionRef.current}`;
      const res = await axiosInstance.get(`backgammon/${tableId}/${v}`);
      ingest(res.data);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        toast.error('table not found');
        onExit();
      }
    }
  }, [tableId, ingest, onExit]);

  // Recursive poll so we can speed up/slow down the cadence on the fly: fast
  // while the opponent is rolling/moving (so you watch it live), slow when idle.
  const pollDelayRef = useRef(POLL_MS);
  useEffect(() => {
    versionRef.current = null;
    setData(null);
    let stop = false;
    let timer = null;
    const loop = async () => {
      await fetchState(false);
      if (!stop) timer = setTimeout(loop, pollDelayRef.current);
    };
    fetchState(true).then(() => { if (!stop) timer = setTimeout(loop, pollDelayRef.current); });
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [tableId, fetchState]);

  useEffect(() => {
    const t = setInterval(() => setTickNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  const state = data && data.state;
  const game = state && state.game;
  const you = state ? state.you : { actions: [] };
  const actions = you.actions || [];
  const mySide = you.side === null || you.side === undefined ? 0 : you.side;
  const oppSide = 1 - mySide;
  const canMove = actions.includes('move');
  const canPass = actions.includes('pass');

  // Reset staged moves whenever a new turn/roll arrives.
  const turnKey = game ? `${state.game_no}:${game.log.length ? game.log[game.log.length - 1].n : 0}:${game.phase}` : '';
  useEffect(() => {
    if (turnKeyRef.current !== turnKey) {
      turnKeyRef.current = turnKey;
      setPending([]);
      setSelected(null);
      setDiceSwap(false);
    }
  }, [turnKey]);

  const logLen = game ? game.log.length : 0;
  const msgLen = data && data.messages ? data.messages.length : 0;
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLen]);
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [msgLen]);

  const startMe = game ? game.board[mySide] : null;
  const startOpp = game ? game.board[oppSide] : null;
  const [curMe, curOpp] = useMemo(() => {
    if (!game) return [null, null];
    if (canMove) return applyMoves(startMe, startOpp, pending);
    // Watch the opponent's checkers move live, as they stage them.
    const lp = game.live_preview;
    if (lp && lp.side === oppSide && game.phase === 'moving' && lp.moves && lp.moves.length) {
      const [oppB, meB] = applyMoves(startOpp, startMe, lp.moves);
      return [meB, oppB];
    }
    return [startMe, startOpp];
  }, [game, canMove, startMe, startOpp, pending, oppSide]);

  const legal = useMemo(() => (
    canMove && game.dice ? legalNextMoves(startMe, startOpp, game.dice, pending) : []
  ), [canMove, game, startMe, startOpp, pending]);
  const totalNeeded = useMemo(() => (
    canMove && game.dice ? maxDiceUsable(startMe, startOpp, game.dice) : 0
  ), [canMove, game, startMe, startOpp]);
  const turnComplete = canMove && pending.length === totalNeeded && legal.length === 0;

  const dests = useMemo(() => (
    canMove && selected !== null ? destinationsFrom(startMe, startOpp, game.dice, pending, selected) : new Map()
  ), [canMove, selected, startMe, startOpp, game, pending]);
  const sources = useMemo(() => Array.from(new Set(legal.map((m) => m[0]))), [legal]);

  const post = useCallback(async (path, body) => {
    setBusy(true);
    try {
      const res = await axiosInstance.post(`backgammon/${tableId}/${path}/`, body || {});
      ingest(res.data);
      return res.data;
    } catch (err) {
      const msg = (err.response && err.response.data && (err.response.data.error || err.response.data.detail)) || 'something broke';
      toast.error(msg);
      fetchState(true);
      return null;
    } finally {
      setBusy(false);
    }
  }, [tableId, ingest, fetchState]);

  const act = useCallback((action, extra) => post('act', { action, data: extra || {} }), [post]);

  const submitMoves = useCallback((moves) => {
    act('move', { moves });
  }, [act]);

  useEffect(() => {
    if (autoConfirm && turnComplete && pending.length && !busy) submitMoves(pending);
  }, [autoConfirm, turnComplete, pending, busy, submitMoves]);

  // Auto-play a turn with exactly one possible outcome (a true forced play):
  // no decision to make, so just play it. Evaluated once per turn.
  useEffect(() => {
    if (!canMove || pending.length || busy || !game.dice) return;
    if (autoForcedRef.current === turnKey) return;
    autoForcedRef.current = turnKey;
    if (!readPref('autoforced', true)) return;
    const plays = legalPlays(startMe, startOpp, game.dice);
    if (plays.length === 1 && plays[0].moves.length) submitMoves(plays[0].moves);
  }, [canMove, pending.length, busy, game, turnKey, startMe, startOpp, submitMoves]);

  // Broadcast your in-progress move so opponents watch each checker live.
  // Fire-and-forget: the response doesn't matter, your board is already staged.
  const previewRef = useRef('');
  useEffect(() => {
    if (!game || !canMove) { previewRef.current = ''; return; }
    const key = JSON.stringify(pending);
    if (previewRef.current === key) return;
    previewRef.current = key;
    axiosInstance.post(`backgammon/${tableId}/preview/`, { moves: pending }).catch(() => {});
  }, [pending, canMove, game, tableId]);

  // Poll fast while the opponent is rolling/moving (live), slow when idle.
  useEffect(() => {
    const oppActive = game && !canMove && state && state.status === 'playing'
      && (game.phase === 'moving' || game.phase === 'pre_roll');
    pollDelayRef.current = oppActive ? FAST_POLL_MS : POLL_MS;
  }, [game, canMove, state]);

  const stage = (seq) => {
    setPending((p) => p.concat(seq));
    setSelected(null);
  };

  // Minimal-click play:
  //  - tap your checker  -> play the left-most legal die (tap again to walk it)
  //  - tap a destination -> land two checkers there if both can legally go,
  //    else one; also powers enter-two-off-the-bar and bear-off-two.
  //  - tap the dice      -> swap which die counts as "left" (see onDice below)
  const playLeftDie = (frm) => {
    const steps = legal.filter((m) => m[0] === frm);
    const rem = diceRemaining(game.dice, pending);
    // Default to playing the larger die first (matches the display); swap flips it.
    const ordered = rem.slice().sort((a, b) => (diceSwap ? a - b : b - a));
    for (const d of ordered) {
      const s = steps.find((m) => m[2] === d);
      if (s) { stage([s]); return true; }
    }
    return false;
  };
  const fillTwo = (target) => {
    const firsts = legal.filter((m) => m[1] === target);
    for (const s1 of firsts) {
      const next = legalNextMoves(startMe, startOpp, game.dice, pending.concat([s1]));
      const s2 = next.find((m) => m[1] === target);
      if (s2) { stage([s1, s2]); return true; }
    }
    if (firsts.length) { stage([firsts[0]]); return true; }
    return false;
  };
  const onPoint = (p) => {
    if (!canMove) return;
    if (sources.includes(p) && playLeftDie(p)) return;   // your checker -> play a die
    if (fillTwo(p)) return;                               // destination -> two here if we can
    if (selected !== null && dests.has(p)) return stage(dests.get(p));
    setSelected(null);
  };
  const onBar = () => {
    if (!canMove || !sources.includes(BAR)) return;
    playLeftDie(BAR);
  };
  const onTray = () => {
    if (canMove) fillTwo(OFF);
  };
  const undo = () => { setPending((p) => p.slice(0, -1)); setSelected(null); };
  const resetTurn = () => { setPending([]); setSelected(null); };

  const sendChat = async (e) => {
    e.preventDefault();
    if (!chat.trim()) return;
    const ok = await post('chat', { text: chat, team_only: teamChat });
    if (ok) setChat('');
  };

  const rematch = async () => {
    setBusy(true);
    try {
      const res = await axiosInstance.post(`backgammon/${tableId}/rematch/`);
      onOpenTable(res.data.id);
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.error) || 'rematch failed');
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="bg-loading">setting up the board…</div>;

  const kind = state.kind;
  const opts = state.options;
  const isChouette = kind === 'chouette';
  const actorName = (side) => (game ? nameOf(state, game.actors[side]) : '');
  const spectator = you.side === null || you.side === undefined;

  // Dice to show.
  let diceShow = null;
  let diceOwner = 'me';
  if (game && game.dice) {
    diceOwner = game.turn === mySide ? 'me' : 'opp';
    // Always show the larger die first (diceSwap flips to smaller-first).
    const vals = game.dice[0] === game.dice[1]
      ? [game.dice[0], game.dice[0], game.dice[0], game.dice[0]]
      : game.dice.slice().sort((a, b) => b - a);
    const rem = canMove ? diceRemaining(game.dice, pending) : vals.slice();
    const remCopy = rem.slice();
    diceShow = vals.map((v) => {
      const i = remCopy.indexOf(v);
      if (i >= 0) { remCopy.splice(i, 1); return { value: v, used: false }; }
      return { value: v, used: true };
    });
    // Tapping the dice swaps their order; show them that way too (doubles are symmetric).
    if (diceSwap && game.dice[0] !== game.dice[1]) diceShow = diceShow.slice().reverse();
  }

  // Last opponent move: highlight its points. Only the opponent's move ever
  // lights up the board -- never your own right after you click. (A spectator
  // has no side, so they see whichever move was most recent.)
  let lastMovePoints = [];
  if (game && !canMove) {
    const lastMove = [...game.log].reverse().find(
      (e) => e.type === 'move' && e.moves && e.moves.length && (spectator || e.side !== mySide));
    if (lastMove) {
      const conv = (p) => (lastMove.side === mySide ? p : 25 - p);
      lastMove.moves.forEach(([f, t]) => {
        if (f !== BAR) lastMovePoints.push(conv(f));
        if (t !== OFF) lastMovePoints.push(conv(t));
      });
    }
  }

  // Opponent's most recent turn, kept on screen (roll + play) until you act, so
  // you can see what they did instead of the board just snapping.
  let oppLastMove = null;
  if (game && game.log && game.log.length) {
    const e = [...game.log].reverse().find((x) => x.type === 'move' && x.side !== mySide && x.dice);
    if (e) {
      const moves = e.moves || [];
      const text = moves.length
        ? moves.map(([f, t]) => `${f === BAR ? 'bar' : f}/${t === OFF ? 'off' : t}`).join(' ')
        : "couldn't come in";
      oppLastMove = { name: nameOf(state, e.pid), d: e.dice, text };
    }
  }
  const showOppMove = !!oppLastMove && game && game.phase === 'pre_roll'
    && (spectator || game.turn === mySide);

  // Cube for the board.
  let cube = null;
  if (game && opts.cube) {
    if (isChouette) {
      const mine = game.cubes && game.cubes[you.pid];
      const cap = game.cubes && game.cubes[game.roles.captain];
      const c = mine || cap;
      if (c) {
        const owner = c.owner === null ? null : ((c.owner === 'box') === (mySide === 0) ? 'me' : 'opp');
        cube = { value: c.value, owner };
      }
    } else {
      const c = game.cube;
      cube = { value: c.value, owner: c.owner === null ? null : (c.owner === mySide ? 'me' : 'opp') };
    }
  }

  // Clocks.
  let clocks = null;
  if (clockBase && clockBase.clock) {
    const c = clockBase.clock;
    const elapsed = c.elapsed + (tickNow - clockBase.at) / 1000;
    clocks = [0, 1].map((side) => {
      if (c.side !== side) return { reserve: c.reserve[side], delay: null, running: false };
      const delayLeft = Math.max(0, c.delay - elapsed);
      const reserve = c.reserve[side] - Math.max(0, elapsed - c.delay);
      return { reserve, delay: delayLeft, running: true };
    });
  }

  const playerBox = (side, position) => {
    const pid = game ? game.actors[side] : state.seats[side];
    const score = pid ? state.scores[pid] : 0;
    const pips = game ? game.pips[side] : 167;
    const clock = clocks && clocks[side];
    const onTurn = game && game.turn === side && ['pre_roll', 'moving'].includes(game.phase);
    return (
      <div className={`bg-player bg-player-${position} ${onTurn ? 'bg-player-turn' : ''}`}>
        <span className={`bg-swatch ${side === mySide ? 'bg-checker-me' : 'bg-checker-opp'}`} />
        <span className="bg-player-name">
          {isChouette ? (side === 0 ? `📦 ${nameOf(state, pid)}` : `👑 ${nameOf(state, pid)} + team`) : nameOf(state, pid)}
        </span>
        {!isChouette && <span className="bg-player-score">{kind === 'match' ? `${score}/${opts.match_length}` : `${score > 0 ? '+' : ''}${score}`}</span>}
        <span className="bg-player-pips">{pips} pips</span>
        {clock && (
          <span className={`bg-clock ${clock.running ? 'bg-clock-running' : ''} ${clock.reserve < 30 ? 'bg-clock-low' : ''}`}>
            {clock.running && clock.delay > 0 ? `${Math.ceil(clock.delay)}s + ` : ''}{fmtClock(clock.reserve)}
          </span>
        )}
      </div>
    );
  };

  const header = (() => {
    if (kind === 'match') {
      return `Match to ${opts.match_length}${opts.stake > 0 ? ` · $${opts.stake} to the winner` : ''}${game && game.crawford ? ' · Crawford game' : (state.crawford_used ? ' · post-Crawford' : '')}`;
    }
    if (kind === 'money') return `Money game${opts.jacoby ? ' · Jacoby' : ''}${opts.beaver ? ' · beavers' : ''}`;
    return `Chouette${opts.jacoby ? ' · Jacoby' : ''}${opts.chouette_mode === 'strict' ? ' · Strict' : ' · Relaxed'}${opts.stake > 0 ? ` · $${opts.stake}/pt` : ''} · ${state.seats.length + state.joining.length} players`;
  })();

  // ---- action panel
  const statusLine = (() => {
    if (state.status === 'waiting') {
      const need = isChouette ? 3 : 2;
      return `waiting for players (${state.seats.length}/${need}${isChouette ? '+' : ''})`;
    }
    if (state.status === 'finished') {
      if (state.winner) return `${nameOf(state, state.winner)} wins the ${kind === 'match' ? 'match' : 'session'}! (${state.finish_reason})`;
      return `table closed (${state.finish_reason})`;
    }
    if (!game) return '';
    const ph = game.phase;
    if (ph === 'game_over') {
      const r = game.result;
      return `${nameOf(state, r.winner)} wins a ${LEVEL[r.level]} — ${r.points} pt${r.points === 1 ? '' : 's'} (${r.reason})`;
    }
    if (ph === 'double_offered') {
      if (isChouette && game.offer.by === 1) {
        const pend = Object.entries(game.offer.responses).filter(([, r]) => r === null).map(([p]) => nameOf(state, p));
        return `team doubles — box to respond to ${pend.join(', ')}`;
      }
      if (isChouette) {
        const pend = Object.entries(game.offer.responses).filter(([, r]) => r === null).map(([p]) => nameOf(state, p));
        return `box doubles — waiting on ${pend.join(', ')}`;
      }
      return `${actorName(game.offer.by)} doubles to ${game.cube.value * 2}`;
    }
    if (ph === 'resign_offered') return `${actorName(game.resign.by)} offers to resign a ${LEVEL[game.resign.level]}`;
    if (ph === 'pre_roll' && game.team_cube) {
      if (opts.chouette_mode === 'strict') {
        const pend = Object.entries(game.team_cube.decisions).filter(([, d]) => d === null).map(([p]) => nameOf(state, p));
        return `team cube — ${pend.length ? `waiting on ${pend.join(', ')}` : 'all in'}`;
      }
      // relaxed: nobody is waited on; the captain's roll is the cutoff.
      return game.team_cube.captain_done ? 'team cube…' : 'captain to roll — double now if you want it';
    }
    if (canPass) return `no way in — you danced ${game.dice[0]}-${game.dice[1]}! tap the dice (or Pass) to hand over`;
    if (canMove) {
      if (turnComplete) return autoConfirm ? 'sending…' : 'confirm your move';
      return `your move — ${game.dice[0]}-${game.dice[1]}${selected !== null ? ' (pick a destination)' : ''}`;
    }
    if (ph === 'pre_roll' && actions.includes('roll')) return 'your turn — roll or double';
    if (ph === 'moving' && game.danced) return `${actorName(game.turn)} danced ${game.dice[0]}-${game.dice[1]} — stuck on the bar`;
    return `${actorName(game.turn)} to ${ph === 'moving' ? 'move' : 'roll'}`;
  })();

  const btn = (label, onClick, kindCls = '', disabled = false) => (
    <button key={label} className={`bg-btn ${kindCls}`} onClick={onClick} disabled={busy || disabled}>{label}</button>
  );

  // Roll / double / confirm all live on the board itself (galaxy-style): ROLL and
  // CONFIRM on the right, DOUBLE on the left.
  const boardRoll = !!game && actions.includes('roll');
  const boardDouble = !!game && actions.includes('double');
  const boardConfirm = !!game && canMove && !autoConfirm && turnComplete;
  const boardDoubleValue = boardDouble
    ? (isChouette ? ((game.cubes[you.pid] || {}).value || 0) * 2 : game.cube.value * 2)
    : null;
  // A double offered TO you shows big in the middle of the board with Take/Drop.
  // (The box facing several team doubles at once keeps the per-player panel list.)
  const boardOffer = !!game && actions.includes('take')
    && !(isChouette && game.offer && game.offer.by === 1);
  const boardOfferValue = boardOffer
    ? (isChouette ? ((game.cubes[you.pid] || {}).value || 0) * 2 : game.cube.value * 2)
    : null;

  const actionButtons = [];
  if (state.status === 'waiting') {
    if (data.is_participant) {
      if (isChouette && data.created_by === nameOf(state, you.pid)) {
        actionButtons.push(btn('Start chouette', () => act('start'), 'bg-btn-primary', state.seats.length < 3));
      }
      actionButtons.push(btn('+ Add bot', () => post('add_bot')));
      actionButtons.push(btn('Leave', async () => { await act('leave'); onExit(); }, 'bg-btn-quiet'));
    } else {
      actionButtons.push(btn('Join', () => post('join'), 'bg-btn-primary'));
    }
  }
  if (isChouette && state.status === 'playing' && !data.is_participant) {
    actionButtons.push(btn('Join chouette (next game)', () => post('join'), 'bg-btn-primary'));
  }
  if (actions.includes('roll') && !boardRoll) actionButtons.push(btn(game.team_cube ? 'Roll (no double)' : 'Roll', () => act('roll'), 'bg-btn-primary'));
  if (actions.includes('double') && !boardDouble) {
    const val = isChouette ? (game.cubes[you.pid] || {}).value : game.cube.value;
    actionButtons.push(btn(`Double${val ? ` → ${val * 2}` : ''}`, () => act('double'), 'bg-btn-cube'));
  }
  if (canPass) actionButtons.push(btn("Can't come in — pass", () => act('pass'), 'bg-btn-primary'));
  if (actions.includes('no_double')) actionButtons.push(btn('No double', () => act('no_double')));
  if (actions.includes('take')) {
    if (isChouette && game.offer.by === 1) {
      Object.entries(game.offer.responses).filter(([, r]) => r === null).forEach(([p]) => {
        const v = game.cubes[p].value;
        actionButtons.push(<span key={`r${p}`} className="bg-btn-group">
          <span className="bg-btn-label">{nameOf(state, p)} ({v}→{v * 2})</span>
          {btn(`Take ${nameOf(state, p)}`, () => act('take', { target: p }), 'bg-btn-primary')}
          {btn(`Drop ${nameOf(state, p)}`, () => act('drop', { target: p }), 'bg-btn-danger')}
        </span>);
      });
    } else if (!boardOffer) {
      actionButtons.push(btn('Take', () => act('take'), 'bg-btn-primary'));
      actionButtons.push(btn('Drop', () => act('drop'), 'bg-btn-danger'));
    }
  }
  if (actions.includes('beaver')) actionButtons.push(btn('Beaver! 🦫', () => act('beaver'), 'bg-btn-cube'));
  if (actions.includes('accept_resign')) {
    actionButtons.push(btn('Accept resignation', () => act('accept_resign'), 'bg-btn-primary'));
    actionButtons.push(btn('Play on', () => act('reject_resign')));
  }
  if (canMove) {
    actionButtons.push(btn('Undo', undo, '', !pending.length));
    actionButtons.push(btn('Reset', resetTurn, 'bg-btn-quiet', !pending.length));
    if (!autoConfirm && !boardConfirm) actionButtons.push(btn('Confirm', () => submitMoves(pending), 'bg-btn-primary', !turnComplete));
  }
  if (actions.includes('next_game')) actionButtons.push(btn('Next game', () => act('next_game'), 'bg-btn-primary'));
  if (state.status === 'finished' && !isChouette && data.is_participant) {
    actionButtons.push(btn(data.rematch_id ? 'Go to rematch' : 'Rematch', () => (data.rematch_id ? onOpenTable(data.rematch_id) : rematch()), 'bg-btn-primary'));
  }

  const resignMenu = actions.includes('resign') && (
    <span className="bg-resign">
      {resignOpen ? (
        <>
          {[1, 2, 3].map((l) => btn(`Resign ${LEVEL[l]}`, () => { setResignOpen(false); act('resign', { level: l }); }, 'bg-btn-danger'))}
          {btn('cancel', () => setResignOpen(false), 'bg-btn-quiet')}
        </>
      ) : btn('Resign…', () => setResignOpen(true), 'bg-btn-quiet')}
    </span>
  );

  const leaveBtn = state.status === 'playing' && data.is_participant && (
    isChouette
      ? btn(you.leaving ? 'leaving after this game' : 'Leave after this game', () => act('leave'), 'bg-btn-quiet', you.leaving)
      : btn(kind === 'match' ? 'Forfeit match' : 'Leave table', () => {
        // eslint-disable-next-line no-alert
        if (window.confirm(kind === 'match' ? 'Forfeit the whole match?' : 'Leave? You lose the current game at the cube value.')) act('forfeit');
      }, 'bg-btn-quiet')
  );

  // ---- chouette panel
  const isHost = !!data && data.created_by === nameOf(state, you.pid);
  const canArrange = isChouette && state.status === 'waiting' && isHost && state.seats.length > 1;
  const moveSeat = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= state.seats.length) return;
    const order = state.seats.slice();
    [order[i], order[j]] = [order[j], order[i]];
    post('reorder', { order });
  };
  const chouettePanel = isChouette && (
    <div className="bg-panel">
      <div className="bg-panel-title">Line</div>
      {canArrange && <div className="bg-hint">drag the line with ↑↓ — top is the box, 2nd is the captain</div>}
      {state.seats.map((pid, i) => {
        const roles = game ? game.roles : null;
        const isBox = roles ? roles.box === pid : i === 0;
        const isCap = roles ? roles.captain === pid : i === 1;
        const c = game && game.cubes ? game.cubes[pid] : null;
        let cubeTxt = '';
        if (c) {
          const owner = c.owner === null ? 'center' : (c.owner === 'box' ? 'box has it' : 'team has it');
          cubeTxt = c.live ? `cube ${c.value} · ${owner}` : 'settled';
        }
        const sc = state.scores[pid] || 0;
        return (
          <div key={pid} className={`bg-line-row ${pid === you.pid ? 'bg-line-you' : ''}`}>
            <span className="bg-line-role">{isBox ? '📦 box' : isCap ? '👑 captain' : `#${i}`}</span>
            <span className="bg-line-name">
              {nameOf(state, pid)}{state.leaving.includes(pid) ? ' (leaving)' : ''}
              {game && (game.muted || []).includes(pid) && (
                <span className="bg-line-muted" title="muted until they cube or take — no coaching the captain">🔇</span>
              )}
            </span>
            <span className="bg-line-cube">{cubeTxt}</span>
            {canArrange ? (
              <span className="bg-line-reorder">
                <button className="bg-seat-move" disabled={i === 0} onClick={() => moveSeat(i, -1)} title="move up">↑</button>
                <button className="bg-seat-move" disabled={i === state.seats.length - 1} onClick={() => moveSeat(i, 1)} title="move down">↓</button>
              </span>
            ) : (
              <span className={`bg-line-score ${sc > 0 ? 'pos' : sc < 0 ? 'neg' : ''}`}>{sc > 0 ? '+' : ''}{sc}</span>
            )}
          </div>
        );
      })}
      {state.joining.map((pid) => (
        <div key={pid} className="bg-line-row bg-line-joining">
          <span className="bg-line-role">next game</span>
          <span className="bg-line-name">{nameOf(state, pid)}</span>
          <span className="bg-line-cube" />
          <span className="bg-line-score">{state.scores[pid] || 0}</span>
        </div>
      ))}
      {game && game.team_cube && opts.chouette_mode === 'strict' && (
        <div className="bg-hint">strict: every teammate must roll — waiting on all before the turn goes</div>
      )}
    </div>
  );

  const logLines = game ? game.log.map((e) => ({ n: e.n, text: describe(state, e), type: e.type })).filter((l) => l.text) : [];
  const onTeam = isChouette && game && game.roles.team.includes(you.pid);

  const boardLabels = game ? {
    top: `${game.pips[oppSide]}`,
    bottom: `${game.pips[mySide]}`,
  } : null;

  return (
    <div className="bg-table">
      <div className="bg-table-head">
        <button className="bg-btn bg-btn-quiet" onClick={onExit}>← lobby</button>
        <span className="bg-table-title">{header}</span>
        <span className="bg-table-sub">table #{tableId} · game {state.game_no || '-'}{spectator && state.status !== 'waiting' ? ' · watching' : ''}</span>
      </div>
      <div className="bg-table-body">
        <div className="bg-board-col">
          {playerBox(oppSide, 'top')}
          <div className="bg-board-wrap">
            {game ? (
              <Board
                me={curMe} opp={curOpp}
                selected={selected}
                targets={Array.from(dests.keys())}
                sources={canMove ? sources : []}
                lastMovePoints={lastMovePoints}
                dice={diceShow}
                diceOwner={diceOwner}
                cube={cube}
                crawford={game.crawford}
                labels={boardLabels}
                onPoint={canMove ? onPoint : null}
                onBar={canMove ? onBar : null}
                onTray={canMove ? onTray : null}
                onDice={
                  canPass
                    ? () => act('pass')
                    : turnComplete && !autoConfirm
                      ? () => submitMoves(pending)
                      : (canMove && game.dice && game.dice[0] !== game.dice[1] ? () => setDiceSwap((s) => !s) : null)
                }
                onRoll={boardRoll ? () => act('roll') : null}
                onConfirm={boardConfirm ? () => submitMoves(pending) : null}
                onDouble={boardDouble ? () => act('double') : null}
                doubleValue={boardDoubleValue}
                offer={boardOffer ? {
                  value: boardOfferValue,
                  title: isChouette && game.offer && game.offer.by === 0 ? 'BOX DOUBLES' : 'DOUBLE',
                  onTake: () => act('take'),
                  onDrop: () => act('drop'),
                } : null}
              />
            ) : (
              <div className="bg-waiting">
                <div>{statusLine}</div>
                <div className="bg-waiting-players">{state.seats.map((p) => nameOf(state, p)).join(' · ')}</div>
              </div>
            )}
          </div>
          {playerBox(mySide, 'bottom')}
          {showOppMove && (
            <div className="bg-oppmove">
              <span className="bg-oppmove-dice">{oppLastMove.d[0]}-{oppLastMove.d[1]}</span>
              <span>{oppLastMove.name} played <b>{oppLastMove.text}</b></span>
            </div>
          )}
          <div className="bg-status">{statusLine}</div>
          <div className="bg-actions">
            {actionButtons}
            {resignMenu}
            {leaveBtn}
          </div>
          {canMove && (
            <div className="bg-prefs">
              <label><input type="checkbox" checked={autoConfirm} onChange={(e) => { setAutoConfirm(e.target.checked); writePref('autoconfirm', e.target.checked); }} /> auto-confirm</label>
              <label><input type="checkbox" checked={autoForced} onChange={(e) => { setAutoForced(e.target.checked); writePref('autoforced', e.target.checked); }} /> auto-play forced</label>
              <span className="bg-hint">tap a checker to play the left die (tap again to walk it) · tap a point to send two there · tap the dice to swap their order{autoConfirm ? '' : ' · tap the dice when done to confirm'}</span>
            </div>
          )}
        </div>
        <div className="bg-side-col">
          {chouettePanel}
          {!isChouette && state.history.length > 0 && (
            <div className="bg-panel">
              <div className="bg-panel-title">Games</div>
              {state.history.slice(-8).map((h) => (
                <div key={h.game_no} className="bg-hist-row">
                  #{h.game_no} {nameOf(state, h.winner)} +{h.points} <span className="bg-hint">{LEVEL[h.level]} · {h.reason}</span>
                </div>
              ))}
            </div>
          )}
          <div className="bg-panel bg-log-panel">
            <div className="bg-panel-title">
              Moves
              {(state.game_no > 1 || (game && game.phase === 'game_over') || state.status === 'finished') && (
                <>
                  <button className="bg-link" onClick={() => setShowReplay(true)}>review games</button>
                  <button className="bg-link" onClick={() => setShowAnalysis(true)}>analysis</button>
                </>
              )}
            </div>
            <div className="bg-log" ref={logRef}>
              {logLines.map((l) => <div key={l.n} className={`bg-log-row bg-log-${l.type}`}>{l.text}</div>)}
            </div>
          </div>
          <div className="bg-panel bg-chat-panel">
            <div className="bg-panel-title">Chat</div>
            <div className="bg-chat" ref={chatRef}>
              {(data.messages || []).map((m) => (
                <div key={m.id} className={`bg-chat-row ${m.team_only ? 'bg-chat-team' : ''}`}>
                  <b>{m.user}</b>{m.team_only ? ' (team)' : ''}: {m.text}
                </div>
              ))}
            </div>
            {isLoggedIn ? (
              <form onSubmit={sendChat} className="bg-chat-form">
                <input value={chat} onChange={(e) => setChat(e.target.value)} placeholder={teamChat ? 'team only…' : 'say something'} maxLength={500} />
                {onTeam && <label className="bg-team-toggle"><input type="checkbox" checked={teamChat} onChange={(e) => setTeamChat(e.target.checked)} /> team</label>}
              </form>
            ) : (
              <div className="bg-hint" style={{ padding: '6px 2px' }}><a className="bg-link" href="/login" style={{ float: 'none' }}>sign in</a> to chat</div>
            )}
          </div>
        </div>
      </div>
      {showReplay && <Replay tableId={tableId} state={state} onClose={() => setShowReplay(false)} />}
      {showAnalysis && <Analysis tableId={tableId} state={state} onClose={() => setShowAnalysis(false)} />}
    </div>
  );
}
